import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronRight,
  Search,
  Radio,
  Clock,
  Ban,
  Loader2,
  RotateCcw,
  ShieldCheck,
  ExternalLink,
  Copy,
  CheckCircle2,
  RefreshCw,
  KeyRound,
  Info,
  Users as UsersIcon,
  Activity,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProviderGuide, googleGuide, metaGuide, genericProviderGuide } from "@/pages/ProviderGuide";

export type ChannelStatus = "live" | "coming_soon" | "disabled";
export type AuthMethod = "oauth2" | "oauth1a" | "app_password" | "bot_token" | "not_implemented" | "deprecated";

interface ChannelRow {
  card_id: string;
  title: string;
  description?: string;
  platform: string;
  account_type?: string;
  group: string;
  region?: string;
  catalog_status: "live" | "coming_soon" | "deprecated";
  provider_id: string | null;
  provider_status: ChannelStatus | null;
  override_status: ChannelStatus | null;
  effective_status: ChannelStatus | "deprecated";
  auth_method: AuthMethod;
  admin_needs_config: boolean;
  shares_credentials_with: string | null;
  redirect_uri: string | null;
  credentials: {
    client_id_masked: string;
    configured: boolean;
    source: "db" | "env" | "none";
    shared_with: string | null;
  };
  connected_users: number;
}

const GROUP_META: Record<string, { label: string; hint: string; order: number }> = {
  meta:      { label: "Meta family",      hint: "Facebook + Instagram + Threads", order: 1 },
  google:    { label: "Google",           hint: "YouTube + Google Business Profile", order: 2 },
  western:   { label: "Western social",   hint: "LinkedIn, X, TikTok, Reddit, Pinterest, Bluesky, Tumblr, Snapchat", order: 3 },
  messaging: { label: "Chat & Messaging", hint: "Telegram, Discord", order: 4 },
  regional:  { label: "Regional",         hint: "VK, WeChat, Weibo, LINE", order: 5 },
};

const AUTH_META: Record<AuthMethod, { label: string; cls: string; hint: string }> = {
  oauth2:         { label: "OAuth 2.0",          cls: "bg-sky-50 border-sky-200 text-sky-700",           hint: "Requires Client ID + Secret" },
  oauth1a:        { label: "OAuth 1.0a",         cls: "bg-sky-50 border-sky-200 text-sky-700",           hint: "Requires Consumer Key + Secret" },
  app_password:   { label: "App Password",       cls: "bg-violet-50 border-violet-200 text-violet-700",  hint: "Users enter their own — no admin config" },
  bot_token:      { label: "Bot Token",          cls: "bg-violet-50 border-violet-200 text-violet-700",  hint: "Users enter their own — no admin config" },
  not_implemented:{ label: "Not implemented",    cls: "bg-amber-50 border-amber-200 text-amber-800",     hint: "No auth flow yet — shown as Coming soon" },
  deprecated:     { label: "API deprecated",     cls: "bg-slate-100 border-slate-200 text-slate-600",    hint: "The provider shut this API off — can only be Disabled" },
};

const statusPill = (s: string) => {
  const map: Record<string, string> = {
    live: "bg-emerald-50 border-emerald-200 text-emerald-700",
    coming_soon: "bg-amber-50 border-amber-200 text-amber-800",
    disabled: "bg-rose-50 border-rose-200 text-rose-700",
    deprecated: "bg-slate-100 border-slate-200 text-slate-600",
  };
  const label = s === "coming_soon" ? "Coming soon" : s.charAt(0).toUpperCase() + s.slice(1);
  return <span className={`sg-chip border ${map[s] || map.deprecated}`}>{label}</span>;
};

// Public developer console URLs per provider (mirrors AdminSetup CONSOLE_LINKS)
const CONSOLE_LINKS: Record<string, { label: string; href: string }> = {
  google: { label: "Google Cloud Console", href: "https://console.cloud.google.com/" },
  meta: { label: "Meta for Developers", href: "https://developers.facebook.com/apps/" },
  threads: { label: "Threads API console", href: "https://developers.facebook.com/apps/" },
  linkedin: { label: "LinkedIn Developer Portal", href: "https://www.linkedin.com/developers/apps" },
  linkedin_company: { label: "LinkedIn Developer Portal", href: "https://www.linkedin.com/developers/apps" },
  pinterest: { label: "Pinterest Developer", href: "https://developers.pinterest.com/apps/" },
  tumblr: { label: "Tumblr Applications", href: "https://www.tumblr.com/oauth/apps" },
  discord: { label: "Discord Developer Portal", href: "https://discord.com/developers/applications" },
  reddit: { label: "Reddit Apps", href: "https://www.reddit.com/prefs/apps" },
  twitter: { label: "X Developer Portal", href: "https://developer.x.com/en/portal/dashboard" },
  tiktok: { label: "TikTok for Developers", href: "https://developers.tiktok.com/apps" },
  vk: { label: "VK for Developers", href: "https://vk.com/apps?act=manage" },
};

// Docs for non-OAuth methods
const AUTH_DOCS: Record<string, { label: string; href: string }> = {
  bluesky_profile: { label: "How to generate a Bluesky App Password", href: "https://bsky.app/settings/app-passwords" },
  telegram_channel: { label: "How to create a Telegram bot & get a token", href: "https://core.telegram.org/bots#creating-a-new-bot" },
};

// ------------------ Small primitives ------------------

const AuthChip: React.FC<{ method: AuthMethod }> = ({ method }) => {
  const m = AUTH_META[method];
  return <span className={`sg-chip border ${m.cls}`} title={m.hint}>{m.label}</span>;
};

const StatusToggle: React.FC<{
  cardId: string; current: ChannelStatus | "deprecated"; deprecated: boolean; onChange: (next: ChannelStatus) => void; pending: boolean;
}> = ({ cardId, current, deprecated, onChange, pending }) => {
  const opts: Array<{ id: ChannelStatus; icon: any; cls: string; label: string }> = [
    { id: "live",        icon: Radio, cls: "text-emerald-700 hover:bg-emerald-50 data-[active=true]:bg-emerald-600 data-[active=true]:text-white", label: "Live" },
    { id: "coming_soon", icon: Clock, cls: "text-amber-700 hover:bg-amber-50 data-[active=true]:bg-amber-500 data-[active=true]:text-white",       label: "Coming soon" },
    { id: "disabled",    icon: Ban,   cls: "text-rose-700 hover:bg-rose-50 data-[active=true]:bg-rose-600 data-[active=true]:text-white",           label: "Disabled" },
  ];
  return (
    <div className="inline-flex items-center rounded-lg border border-[color:var(--sg-border)] bg-white p-0.5 gap-0.5" role="group"
          data-testid={`chan-toggle-${cardId}`}>
      {opts.map((o) => {
        const active = current === o.id;
        const btnDisabled = active || pending || (deprecated && o.id !== "disabled");
        return (
          <button
            key={o.id}
            type="button"
            data-active={active}
            data-testid={`chan-status-${cardId}-${o.id}`}
            disabled={btnDisabled}
            onClick={() => onChange(o.id)}
            title={deprecated && o.id !== "disabled" ? "API deprecated — can only be Disabled" : o.label}
            className={`inline-flex items-center gap-1 rounded-md text-[11px] h-7 px-2 font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${o.cls}`}
          >
            {pending && active === false ? <Loader2 className="h-3 w-3 animate-spin" /> : <o.icon className="h-3 w-3" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

const CopyChip: React.FC<{ value: string; testId: string }> = ({ value, testId }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      data-testid={testId}
      className="w-full font-mono text-[11px] bg-white border border-[color:var(--sg-border)] rounded px-2 py-1.5 flex items-center gap-2 hover:bg-slate-50"
    >
      <span className="truncate flex-1 text-left">{value}</span>
      {copied ? <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" /> : <Copy className="h-3 w-3 text-[color:var(--sg-muted)] shrink-0" />}
    </button>
  );
};

// ------------------ Expanded row body ------------------

const CredentialsForm: React.FC<{ row: ChannelRow; onSaved: () => void }> = ({ row, onSaved }) => {
  const [cid, setCid] = React.useState("");
  const [csec, setCsec] = React.useState("");

  const save = useMutation({
    mutationFn: async () => {
      if (!row.provider_id) throw new Error("No provider");
      if (row.provider_id === "google") {
        return (await api.put("/admin/config", {
          google_client_id: cid.trim() || undefined,
          google_client_secret: csec.trim() || undefined,
        })).data;
      }
      if (row.provider_id === "meta") {
        return (await api.put("/admin/config", {
          meta_app_id: cid.trim() || undefined,
          meta_app_secret: csec.trim() || undefined,
        })).data;
      }
      return (await api.put(`/admin/oauth-providers/${row.provider_id}`, {
        client_id: cid.trim(),
        client_secret: csec.trim(),
      })).data;
    },
    onSuccess: () => {
      toast.success(`${row.title} credentials saved`);
      setCsec("");
      onSaved();
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const canSave = row.provider_id === "google" || row.provider_id === "meta"
    ? Boolean(cid.trim() || csec.trim())
    : Boolean(cid.trim());

  return (
    <div className="grid md:grid-cols-2 gap-3">
      <div>
        <Label className="text-[10px] uppercase tracking-wider">
          {row.provider_id === "meta" ? "App ID" : "Client ID"}
        </Label>
        <Input
          data-testid={`chan-cid-${row.card_id}`}
          value={cid}
          onChange={(e) => setCid(e.target.value)}
          placeholder={row.credentials.configured ? `Currently: ${row.credentials.client_id_masked || "(set)"}` : "Paste here"}
          className="h-9"
        />
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider">
          {row.provider_id === "meta" ? "App Secret" : "Client Secret"}
        </Label>
        <Input
          data-testid={`chan-csec-${row.card_id}`}
          value={csec}
          onChange={(e) => setCsec(e.target.value)}
          placeholder={row.credentials.configured ? "(leave blank to keep)" : "Paste here"}
          type="password"
          className="h-9"
        />
      </div>
      <div className="md:col-span-2 flex justify-end">
        <Button
          data-testid={`chan-save-${row.card_id}`}
          size="sm"
          disabled={save.isPending || !canSave}
          onClick={() => save.mutate()}
          className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white h-8"
        >
          {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />}
          Save credentials
        </Button>
      </div>
    </div>
  );
};

const TestConnectionButton: React.FC<{ row: ChannelRow }> = ({ row }) => {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<{ ok: boolean; message: string } | null>(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = (await api.post(`/admin/catalog-cards/${row.card_id}/test-connection`)).data;
      setResult({ ok: !!r.ok, message: r.message });
      if (r.ok) toast.success(`${row.title}: ${r.message}`);
      else toast.warning(`${row.title}: ${r.message}`);
    } catch (e: any) {
      setResult({ ok: false, message: e?.response?.data?.detail || "Test failed" });
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button size="sm" variant="outline" onClick={run} disabled={busy} className="h-7 text-xs"
              data-testid={`chan-test-${row.card_id}`}>
        {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Activity className="h-3 w-3 mr-1" />}
        Test connection
      </Button>
      {result && (
        <span
          className={`text-[11px] ${result.ok ? "text-emerald-700" : "text-amber-700"}`}
          data-testid={`chan-test-result-${row.card_id}`}
        >
          {result.ok ? "✓" : "⚠"} {result.message}
        </span>
      )}
    </div>
  );
};

const ChannelRowExpanded: React.FC<{ row: ChannelRow; onRefresh: () => void }> = ({ row, onRefresh }) => {
  const authDoc = AUTH_DOCS[row.card_id];
  const consoleLink = row.provider_id ? CONSOLE_LINKS[row.provider_id] : null;

  if (row.auth_method === "deprecated") {
    return (
      <div className="text-xs text-[color:var(--sg-muted)] bg-slate-50 rounded-md p-3">
        {row.title} was shut off by the platform provider. It can only be set to <b>Disabled</b>.
      </div>
    );
  }
  if (row.auth_method === "app_password" || row.auth_method === "bot_token") {
    return (
      <div className="rounded-md bg-violet-50/60 border border-violet-200 p-3 text-xs space-y-2">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-violet-700 shrink-0 mt-0.5" />
          <div>
            <b className="text-violet-900">{row.title}</b> uses{" "}
            <b>{row.auth_method === "app_password" ? "per-user App Passwords" : "per-user Bot Tokens"}</b> — there
            is nothing to configure here. Each SociBaba user enters their own credentials on the{" "}
            <a href="/platforms" className="underline">Platforms page</a> when they connect.
          </div>
        </div>
        {authDoc && (
          <a
            href={authDoc.href}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-xs text-violet-800 hover:underline"
            data-testid={`chan-authdoc-${row.card_id}`}
          >
            {authDoc.label} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }
  if (row.auth_method === "not_implemented") {
    return (
      <div className="text-xs text-[color:var(--sg-muted)] bg-amber-50/40 border border-amber-200 rounded-md p-3">
        No auth flow implemented for <b>{row.title}</b> yet. Flip to <b>Coming soon</b> to preview it on the
        Platforms page, or leave it <b>Disabled</b> until we ship support.
      </div>
    );
  }

  // OAuth 2.0 / OAuth 1.0a
  const shared = row.shares_credentials_with;
  const backend = (typeof window !== "undefined" ? window.location.origin : "");
  const redirectUri = row.redirect_uri || (
    row.provider_id === "google" ? `${backend}/api/oauth/google/callback` :
    row.provider_id === "meta" ? `${backend}/api/oauth/meta/callback` :
    row.provider_id ? `${backend}/api/oauth/generic/${row.provider_id}/callback` :
    ""
  );
  return (
    <div className="space-y-4">
      {shared === "meta" && row.provider_id !== "meta" && (
        <div className="rounded-md bg-sky-50 border border-sky-200 p-3 text-xs text-sky-900">
          <Info className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          <b>{row.title}</b> shares Meta App ID + Secret with Facebook Page. Editing them on the Facebook Page row will affect this channel too.
        </div>
      )}
      {!shared && (
        <>
          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <div className="rounded-md border border-[color:var(--sg-border)] bg-white p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">Redirect URI</div>
              {row.redirect_uri || redirectUri ? (
                <CopyChip value={row.redirect_uri || redirectUri} testId={`chan-redirect-${row.card_id}`} />
              ) : (
                <span className="font-mono text-[10px] text-[color:var(--sg-muted)]">—</span>
              )}
            </div>
            <div className="rounded-md border border-[color:var(--sg-border)] bg-white p-2.5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">Effective client_id</div>
                <div className="font-mono text-[11px] text-[color:var(--sg-fg)] mt-0.5" data-testid={`chan-effective-${row.card_id}`}>
                  {row.credentials.client_id_masked || <span className="text-[color:var(--sg-muted)]">not set</span>}
                </div>
                <div className="text-[10px] text-[color:var(--sg-muted)] mt-0.5">
                  {row.credentials.configured
                    ? <>from <b>{row.credentials.source}</b> · secret set</>
                    : <span className="text-amber-800">not configured</span>}
                </div>
              </div>
              <ShieldCheck className={`h-4 w-4 ${row.credentials.configured ? "text-emerald-500" : "text-slate-400"}`} />
            </div>
          </div>
          <CredentialsForm row={row} onSaved={onRefresh} />
          <TestConnectionButton row={row} />
        </>
      )}
      {consoleLink && (
        <ProviderGuide
          providerId={row.card_id}
          steps={
            row.provider_id === "google" ? googleGuide(redirectUri)
              : row.provider_id === "meta" ? metaGuide(redirectUri)
                : genericProviderGuide(row.provider_id!, consoleLink.label, consoleLink.href, redirectUri, [])
          }
        />
      )}
    </div>
  );
};

// ------------------ Main ------------------

export const ChannelManager: React.FC = () => {
  const qc = useQueryClient();
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [pendingId, setPendingId] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState("");

  const q = useQuery<{ items: ChannelRow[] }>({
    queryKey: ["channel-manager"],
    queryFn: async () => (await api.get("/admin/catalog-cards")).data,
  });

  const setStatus = useMutation({
    mutationFn: async (v: { card_id: string; status: ChannelStatus }) =>
      (await api.put(`/admin/catalog-cards/${v.card_id}/status`, { status: v.status })).data,
    onSuccess: (_d, v) => {
      toast.success(`${v.card_id}: ${v.status === "coming_soon" ? "Coming soon" : v.status}`);
      qc.invalidateQueries({ queryKey: ["channel-manager"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      qc.invalidateQueries({ queryKey: ["oauth-effective"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Could not update"),
    onSettled: () => setPendingId(null),
  });

  const clearOverride = useMutation({
    mutationFn: async (card_id: string) =>
      (await api.delete(`/admin/catalog-cards/${card_id}/status`)).data,
    onSuccess: (_d, id) => {
      toast.success(`${id}: override cleared`);
      qc.invalidateQueries({ queryKey: ["channel-manager"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Could not clear"),
    onSettled: () => setPendingId(null),
  });

  const toggleExpand = (cid: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(cid)) next.delete(cid);
    else next.add(cid);
    return next;
  });

  const filtered = React.useMemo(() => {
    const items = q.data?.items || [];
    if (!filter.trim()) return items;
    const f = filter.trim().toLowerCase();
    return items.filter((r) =>
      r.card_id.includes(f)
      || r.title.toLowerCase().includes(f)
      || r.platform.includes(f)
      || (r.provider_id || "").includes(f)
      || r.auth_method.includes(f)
    );
  }, [q.data, filter]);

  const grouped = React.useMemo(() => {
    const map = new Map<string, ChannelRow[]>();
    for (const r of filtered) {
      const arr = map.get(r.group) || [];
      arr.push(r);
      map.set(r.group, arr);
    }
    return Array.from(map.entries()).sort(
      (a, b) => (GROUP_META[a[0]]?.order || 99) - (GROUP_META[b[0]]?.order || 99),
    );
  }, [filtered]);

  const totalCount = q.data?.items.length || 0;
  const liveCount = (q.data?.items || []).filter((r) => r.effective_status === "live").length;
  const csCount = (q.data?.items || []).filter((r) => r.effective_status === "coming_soon").length;
  const disCount = (q.data?.items || []).filter((r) => r.effective_status === "disabled").length;

  return (
    <div className="rounded-xl border border-[color:var(--sg-border)] bg-white p-4" data-testid="channel-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h2 className="text-base font-semibold text-[color:var(--sg-fg)]">Channels</h2>
          <p className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Every social channel SociBaba supports. Click a row to configure credentials, toggle availability, and test.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          <span className="sg-chip bg-emerald-50 border-emerald-200 text-emerald-700">{liveCount} live</span>
          <span className="sg-chip bg-amber-50 border-amber-200 text-amber-800">{csCount} coming soon</span>
          <span className="sg-chip bg-rose-50 border-rose-200 text-rose-700">{disCount} disabled</span>
          <span className="sg-chip bg-slate-100 border-slate-200 text-slate-600">{totalCount} total</span>
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-[color:var(--sg-muted)]" />
        <Input
          data-testid="channel-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter — try 'meta', 'oauth2', 'bluesky', 'reddit', 'not_implemented'…"
          className="pl-8 h-9"
        />
      </div>

      {q.isLoading ? (
        <div className="sg-skeleton h-40" />
      ) : filtered.length === 0 ? (
        <div className="text-xs text-[color:var(--sg-muted)] border border-dashed border-[color:var(--sg-border)] rounded-md p-4 text-center">
          No channels match “{filter}”.
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([group, rows]) => (
            <div key={group} data-testid={`chan-group-${group}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--sg-fg)]">
                  {GROUP_META[group]?.label || group}
                </div>
                <div className="text-[10px] text-[color:var(--sg-muted)]">
                  {GROUP_META[group]?.hint} · {rows.length} channels
                </div>
              </div>
              <div className="border border-[color:var(--sg-border)] rounded-md divide-y divide-[color:var(--sg-border)] overflow-hidden">
                {rows.map((r) => {
                  const isExpanded = expanded.has(r.card_id);
                  const deprecated = r.auth_method === "deprecated";
                  return (
                    <div key={r.card_id} data-testid={`chan-row-${r.card_id}`}>
                      <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 sm:gap-3 px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => toggleExpand(r.card_id)}
                          className="p-1 text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)]"
                          data-testid={`chan-expand-${r.card_id}`}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExpand(r.card_id)}
                          className="min-w-0 text-left"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-sm text-[color:var(--sg-fg)]">{r.title}</span>
                            <AuthChip method={r.auth_method} />
                            {statusPill(r.effective_status)}
                            {r.override_status && (
                              <span className="sg-chip bg-violet-50 border-violet-200 text-violet-700" data-testid={`chan-override-${r.card_id}`}>
                                Overridden
                              </span>
                            )}
                            {r.credentials.configured && (r.auth_method === "oauth2" || r.auth_method === "oauth1a") && (
                              <span className="sg-chip bg-emerald-50 border-emerald-200 text-emerald-700"
                                    title={`Credentials from ${r.credentials.source}`}
                                    data-testid={`chan-configured-${r.card_id}`}>
                                <CheckCircle2 className="h-3 w-3" /> Configured
                              </span>
                            )}
                            {r.connected_users > 0 && (
                              <span className="sg-chip bg-slate-100 border-slate-200 text-slate-700" title="Connected user accounts (all workspaces)"
                                    data-testid={`chan-connected-${r.card_id}`}>
                                <UsersIcon className="h-3 w-3" /> {r.connected_users}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 truncate">
                            <span className="font-mono">{r.card_id}</span>
                            {r.provider_id && <> · provider <span className="font-mono">{r.provider_id}</span></>}
                          </div>
                        </button>
                        <StatusToggle
                          cardId={r.card_id}
                          current={r.effective_status}
                          deprecated={deprecated}
                          pending={setStatus.isPending && pendingId === r.card_id}
                          onChange={(next) => { setPendingId(r.card_id); setStatus.mutate({ card_id: r.card_id, status: next }); }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!r.override_status || (clearOverride.isPending && pendingId === r.card_id)}
                          onClick={() => { setPendingId(r.card_id); clearOverride.mutate(r.card_id); }}
                          data-testid={`chan-reset-${r.card_id}`}
                          className="h-7 text-[11px]"
                          title={r.override_status ? "Clear override" : "No override set"}
                        >
                          {clearOverride.isPending && pendingId === r.card_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RotateCcw className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-2 bg-[color:var(--sg-muted-bg)]/40" data-testid={`chan-body-${r.card_id}`}>
                          <ChannelRowExpanded
                            row={r}
                            onRefresh={() => qc.invalidateQueries({ queryKey: ["channel-manager"] })}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChannelManager;
